import dayjs from "dayjs"
import { CurrencyCode } from "../../types/currency"

/* eslint-disable */
async function getCSRFToken() {
	let csrfResponse = await fetch(`${process.env.REACT_APP_BASE_API}/csrfToken`)
	csrfResponse = await csrfResponse.json()
	return (csrfResponse as any)['csrf']
}

/**
 * Maps currency codes to their appropriate locales per ISO 4217 and regional conventions.
 * - GBP: en-GB (UK standard)
 * - USD: en-US (US standard)
 * - EUR: en-IE (English-speaking EUR country, maintains consistent locale naming)
 * This ensures currency formatting follows regional conventions while keeping the app in English.
 */
const getLocaleForCurrency = (currencyCode: string): string => {
	switch (currencyCode) {
		case 'USD':
			return 'en-US'
		case 'EUR':
			return 'en-IE'
		case 'GBP':
		default:
			return 'en-GB'
	}
}

const createCurrencyFormatter = (currencyCode: string) => {
	const locale = getLocaleForCurrency(currencyCode)
	return new Intl.NumberFormat(locale, {
		style: 'currency',
		currency: currencyCode,
	})
}

// currency arrives as a cents integer from the backend
// and needs to be converted to an actual float before
// being put into the `NumericFormat` input
const centsToCurrency = (cents?: number): number | undefined => {
	if (!cents) return cents
	return cents / 100
}

const getCurrencySymbol = (currencyCode: CurrencyCode) => {
	switch(currencyCode) {
		case 'GBP':
			return '£';
		case 'EUR':
			return '€';
		case 'USD':
			return '$';
	}
}
// The `NumericFormat` outputs a string or number with commas an
// a period as a decimal separator, we need to convert this back to
// cents (integer) before sending to the backend
const currencyToCents = (currency: string | number): number => {
	currency = Number(currency.toString().replaceAll(',', ''))
	return currency * 100
}

const formatEmissionCO2Kg = (value: number): string => {
	const formatter = new Intl.NumberFormat('en-UK', {
		style: 'unit',
		unit: 'kilogram',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})
	return formatter.format(value)
}

const validateEmail = (email: string) => {
	if (email === '') {
		return false
	}
	const regexp = new RegExp(
		/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
	)
	return regexp.test(email)
}

// AdonisJS validation failures (E_VALIDATION_FAILURE) return `message` as an object
// keyed by field name (e.g. `{ email: ["email must be a valid email address"] }`),
// not a string - so `err.body?.message` can't be rendered directly as a React child.
// This normalises any shape (string, field-keyed object, array) into a display string.
const getApiErrorMessage = (err: any, fallback: string): string => {
	const raw = err?.body?.message ?? err?.body?.error
	if (!raw) {
		return fallback
	}
	if (typeof raw === 'string') {
		return raw
	}
	if (typeof raw === 'object') {
		try {
			const flatten = (value: any): string => {
				if (typeof value === 'string') return value
				if (Array.isArray(value)) return value.map(flatten).join(', ')
				if (value && typeof value === 'object') return Object.values(value).map(flatten).join(', ')
				return String(value)
			}
			const message = Object.entries(raw)
				.map(([field, value]) => `${field}: ${flatten(value)}`)
				.join('; ')
			return message || fallback
		} catch {
			return fallback
		}
	}
	return fallback
}

const getParams = (paramsParameter: any) => {
	let params = new URLSearchParams(paramsParameter)
	let keysForDel: any = []
	params.forEach((value, key) => {
		if (value == '') {
			keysForDel.push(key)
		}
	})

	keysForDel.forEach((key: any) => {
		params.delete(key)
	})
	return params
}

const formatDate = (dateString: string) => {
	return dayjs(dateString).format('DD/MM/YY')
}

// A null training session start_date means the session starts immediately
// (Decision 11) - shown as "Immediately (<created date>)" instead of blank.
const formatScheduledToStart = (startDate: string | null | undefined, createdAt: string | null | undefined) => {
	if (startDate) {
		return formatDate(startDate)
	}
	if (createdAt) {
		return `Immediately (${formatDate(createdAt)})`
	}
	return undefined
}

const formatControlRequestStatus = (status: string = '') => {
	if(!status) {
		return '';
	}
	if(status === 'rejected') {
		status = 'request rejected';
	}
	const withSpaces = status.replace(/_+/g, ' ');
	const upperCase = withSpaces[0].toUpperCase() + withSpaces.slice(1);
	return upperCase;
}

const formatReportStatus = formatControlRequestStatus;

// Canonical formatter for values whose magnitude varies wildly (ratios, intensities,
// totals) - values >= 1 get thousand separators with up to 2 decimal places and no
// forced padding, values < 1 get 2 significant digits so small ratios stay readable
// instead of collapsing to "0.00" or trailing off into false 6-decimal precision.
// e.g. 252.313977 -> "252.31", 0.000123456 -> "0.00012", 19562877 -> "19,562,877"
const formatAdaptiveNumber = (value: number, locale: string = 'en-GB'): string => {
	if (!Number.isFinite(value)) return String(value)
	if (value === 0) return '0'

	const formatter = Math.abs(value) >= 1
		? new Intl.NumberFormat(locale, { maximumFractionDigits: 2, minimumFractionDigits: 0 })
		: new Intl.NumberFormat(locale, { maximumSignificantDigits: 2, minimumSignificantDigits: 2 })

	return formatter.format(value)
}

export {
	getCSRFToken,
	centsToCurrency,
	currencyToCents,
	createCurrencyFormatter,
	getLocaleForCurrency,
	formatEmissionCO2Kg,
	formatAdaptiveNumber,
	validateEmail,
	getApiErrorMessage,
	getParams,
	getCurrencySymbol,
	formatDate,
	formatScheduledToStart,
	formatControlRequestStatus,
	formatReportStatus,
}
